import React, { useState } from 'react';
import './About.css';
import cv from '../assets/Md_Ruman_Islam_CV.pdf';
import {
  FaEnvelope,
  FaGithub,
  FaGitlab,
  FaGlobe,
  FaGoogleScholar,
  FaLinkedin,
} from 'react-icons/fa6';
import { ImProfile } from 'react-icons/im';

const FOCUS_AREAS = [
  {
    title: 'Adaptive Communication Intelligence',
    description:
      'Designing machine learning and deep reinforcement learning models for congestion-aware control, adaptive data transmission, and learning-enabled network optimization.',
  },
  {
    title: 'Digital Twins for Cyber-Physical Systems',
    description:
      'Developing intelligent twin frameworks that support monitoring, prediction, and decision-making across real-time and autonomous environments.',
  },
  {
    title: 'Autonomous Platforms in the Loop',
    description:
      'Building and evaluating UAV and vehicle systems with ROS, Autoware, sensing pipelines, and field-oriented deployment workflows.',
  },
];

const RESEARCH_INTERESTS = [
  'Artificial intelligence for real-time communication and data processing',
  'Deep reinforcement learning for adaptive communication and control systems',
  'Intelligent digital twins for cyber-physical systems',
  '6G networks and autonomous systems including UAVs and autonomous vehicles',
];

const CONTACT_LINKS = [
  { label: 'Website', value: 'rumanislam.me', href: 'https://rumanislam.me', icon: FaGlobe },
  { label: 'LinkedIn', value: 'linkedin.com/in/ruman-i', href: 'https://linkedin.com/in/ruman-i', icon: FaLinkedin },
  { label: 'GitHub', value: 'github.com/ruman23', href: 'https://github.com/ruman23', icon: FaGithub },
  { label: 'Google Scholar', value: 'Scholar Profile', href: 'https://scholar.google.com/citations?user=cJZu7JgAAAAJ&hl=en&authuser=1', icon: FaGoogleScholar },
];

const SOCIAL_LINKS = [
  { label: 'LinkedIn', href: 'https://linkedin.com/in/ruman-i', icon: FaLinkedin },
  { label: 'GitHub', href: 'https://github.com/ruman23', icon: FaGithub },
  { label: 'GitLab', href: 'https://gitlab.com/ruman23', icon: FaGitlab },
  { label: 'Google Scholar', href: 'https://scholar.google.com/citations?user=cJZu7JgAAAAJ&hl=en&authuser=1', icon: FaGoogleScholar },
  { label: 'Curriculum Vitae', href: cv, icon: ImProfile },
];

function About() {
  const [copyMessage, setCopyMessage] = useState('');

  const handleEmailClick = (email) => {
    navigator.clipboard.writeText(email);
    setCopyMessage(`Copied ${email}`);
    setTimeout(() => setCopyMessage(''), 2000);
  };

  return (
    <section id="about" className="site-section" aria-labelledby="about-heading">
      <div className="site-section-inner about-shell">
        <div className="section-heading">
          <p className="section-kicker">Research Profile</p>
          <h2 id="about-heading" className="section-title">Current Directions and Collaboration Areas</h2>
          <p className="section-intro">
            My research studies how learning-enabled systems adapt communication
            and control under real-world constraints. I combine modeling,
            simulation, system integration, and deployment-oriented evaluation.
          </p>
        </div>

        <div className="about-layout">
          <div className="about-main">
            <div className="about-focus-grid">
              {FOCUS_AREAS.map((item) => (
                <article key={item.title} className="about-focus-card card-surface">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </div>

          <aside className="about-sidebar card-surface">
            <div className="about-sidebar-block">
              <h3>Research Interests</h3>
              <ul className="about-interest-list">
                {RESEARCH_INTERESTS.map((interest) => (
                  <li key={interest}>{interest}</li>
                ))}
              </ul>
            </div>

            <div className="about-sidebar-block">
              <h3>Contact</h3>
              {copyMessage && (
                <p className="copy-message" role="status" aria-live="polite">
                  {copyMessage}
                </p>
              )}

              <button type="button" className="about-contact-row" onClick={() => handleEmailClick('mail2ruman.i@gmail.com')}>
                <FaEnvelope />
                <span>
                  <strong>Primary Email</strong>
                  <span>mail2ruman.i@gmail.com</span>
                </span>
              </button>

              <button type="button" className="about-contact-row" onClick={() => handleEmailClick('mdrumanislam@unomaha.edu')}>
                <FaEnvelope />
                <span>
                  <strong>UNO Email</strong>
                  <span>mdrumanislam@unomaha.edu</span>
                </span>
              </button>

              {CONTACT_LINKS.map(({ label, value, href, icon: Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="about-contact-row"
                >
                  <Icon />
                  <span>
                    <strong>{label}</strong>
                    <span>{value}</span>
                  </span>
                </a>
              ))}
            </div>

            <div className="about-sidebar-block">
              <h3>Profiles</h3>
              <div className="about-socials" aria-label="Profiles and CV">
                {SOCIAL_LINKS.map(({ label, href, icon: Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    title={label}
                  >
                    <Icon />
                  </a>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

export default About;
